/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { SubmitReviewButton } from '@/features/reviews/components/SubmitReviewButton/SubmitReviewButton';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { Text, useNotification, View } from '@az/starc-ui';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useRef, useState } from 'react';
import { Rating } from '../../../reviews/components/SubmitProductReviewModal/components/Rating/Rating';
import { useMyProfile } from '@/features/myProfile/api/getMyProfileV2';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { useReviewFormData } from '@/features/reviews/api/getReviewFormData';
import { routePaths } from '@/constants/routePaths';
import logger from '@/utils/logger';
import { CUSTOMER_REVIEWS_HASH } from '../../constants/sectionHash';
import { normalizeErrorForLogging } from '@/utils/errorsHandling/normalizeErrorForLogging';

const labelMap = {
  noReviews: 'label_reviews_noReviewsFromCustomers',
  beTheFirst: 'label_reviews_beTheFirst',
  writeAReview: 'label_reviews_writeAReview2',
  errorNotificationTitle: 'label_reviews_submit_reviewErrorNotificationTitle',
  errorNotificationText: 'label_reviews_submit_errorNotificationText',
};

type Props = {
  productId: string;
};

export const NoReviews = ({ productId }: Props) => {
  const labels = useLabels(labelMap);
  const { data } = useProductDetails();
  const { data: myProfile } = useMyProfile();
  const preferredVehicle = usePreferredVehicle();

  const button = useRef<HTMLButtonElement>(null);
  const [rating, setRating] = useState<number | null>(null);

  const notification = useNotification();

  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const { mutateAsync: getReviewFormData } = useReviewFormData({
    productId,
  });

  const openSubmitReview = async (stars: number | null) => {
    try {
      if (!productId) {
        return;
      }
      setRating(stars);
      const result = await getReviewFormData();
      if (result.hasSubmitted) {
        const id = notification.show({
          type: 'error',
          title: labels.errorNotificationTitle,
          text: labels.errorNotificationText,
          position: 'top-end',
          hasCloseButton: true,
          onClose: () => notification.hide(id),
        });
        setRating(null);
        return;
      }

      const prevUrl = !legacyRouter.asPath.endsWith(CUSTOMER_REVIEWS_HASH)
        ? `${legacyRouter.asPath}${CUSTOMER_REVIEWS_HASH}`
        : undefined;

      const pathname = `${routePaths.writeReviewPath}/${productId}`;
      const params = new URLSearchParams({
        prevUrl: prevUrl || '',
        pageType: 'ProductDetail',
        rating: String(stars),
      }).toString();
      void router.push(`${pathname}?${params}`);
    } catch (error) {
      logger.error({
        message: 'Failed to get review form data',
        meta: {
          source: 'SubmitReviewButton',
          error: normalizeErrorForLogging(error),
        },
      });
    }
  };

  return (
    <View>
      <Text variant="main-body" weight="bold" className={styles.noReviews}>
        {labels.noReviews}
      </Text>
      <View padding={0.5} />
      <Text variant="small-body" className={styles.smallBody}>
        {labels.beTheFirst}
      </Text>
      <View className={styles.starsContainer}>
        <Rating
          className={styles.stars}
          iconClassName={styles.star}
          inputClassName={styles.input}
          id="rating"
          labelID="rating-label"
          name="rating"
          onChange={(stars) => openSubmitReview(stars)}
          value={rating}
        />
      </View>
      <View direction={'row'} justify={{ s: 'center', m: 'start' }}>
        <SubmitReviewButton
          ref={button}
          productId={data?.product?.itemId}
          pageType="ProductDetail"
          defaultValues={{
            useremail: myProfile?.email,
            usernickname: myProfile?.firstName,
            vehicle: preferredVehicle?.vehicleName,
            rating,
          }}
          label={
            <Text variant="small-body" weight="bold" className={styles.smallBody}>
              {labels.writeAReview}
            </Text>
          }
          variant="pill-secondary"
          onClose={() => setRating(null)}
          productName={data?.product?.itemDescription}
          productImageUrl={data?.product?.itemImageUrl}
        />
      </View>
    </View>
  );
};
